<template>
  <div>
    <section class="application-create__form">
      <div class="container-xxxl">
        <div class="row">
          <div class="col-xxl-6 offset-xxl-3">
            <h3 class="application__form__title">{{ $lang.app.DOCUMENTS }}</h3>
            <div class="application__form__block">
              <h4 class="add__fields__title space__between items-center">{{ $lang.app.DOCUMENTS }}
                <div v-if="editable">
                  <button type="button" class="button button--default button--bordered"
                          @click="$emitter.emit('open-applicant-application-document-create-modal', documents)">
                    <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
                      <use xlink:href="@/assets/v2/img/sprite.svg#icon__plus"></use>
                    </svg>
                    <span class="button__text">{{ $lang.app.DOCUMENT }}</span>
                  </button>
                  <button v-if="documents.some(d => d.signDate === null)" type="button"
                          class="button button--default button--bordered ml-4"
                          @click="$emitter.emit('open-sign-all-documents-modal', {applicationId: application.id})">{{ $lang.app.SIGN_ALL }}</button>
                </div>
              </h4>
              <div class="add__field__list">
                <div class="row">
                  <div v-for="document in documents" :key="document.id">
                    <Collapse class="add__field__item">
                      <template v-slot:title>
                        <div class="add__field__header mb-0">
                          {{ document.docType?.name }}
                        </div>
                      </template>
                      <template v-slot:content>
                        <div>
                          <div v-if="document.surname || document.name || document.patronymic" class="stack__list">
                            <div class="stack__label">{{ $lang.app.FULL_NAME }}</div>
                            <div class="stack__value">{{ document.surname }} {{ document.name }} {{ document.patronymic }}</div>
                          </div>
                          <div v-if="document.certificateNo" class="stack__list">
                            <div class="stack__label">{{ $lang.app.CERTIFICATE_NUMBER }}</div>
                            <div class="stack__value">{{ document.certificateNo }}</div>
                          </div>
                          <div class="stack__list">
                            <div class="stack__label">{{ $lang.app.FILES }}</div>
                            <div v-for="(docFile, index) in document.documentsFiles" :key="index" class="flex justify-between items-center mb-3">
                              <button type="button" class="file__item w-full" @click="loadDocumentFile(document, docFile)">
                                <svg class="flex-shrink-0" viewBox="0 0 24 24" width="24" height="24">
                                  <use xlink:href="@/assets/v2/img/sprite.svg#icon__file__pdf"></use>
                                </svg>
                                <span class="max-w-lg file__item__name break-words">
                                  {{ docFile.file.filename }}
                                </span>
                              </button>
                              <button v-if="editable" @click="removeDocumentFile(docFile)" type="button" :title="$lang.app.DELETE"
                                      class="flex-shrink-0 text-gray-300 ml-2">
                                <svg class="fill-current" viewBox="0 0 24 24" width="24" height="24">
                                  <use xlink:href="@/assets/v2/img/sprite.svg#icon__remove"></use>
                                </svg>
                              </button>
                            </div>
                            <button v-if="editable" type="button" class="button button--default button--bordered button--full mb2"
                                    @click="$emitter.emit('open-add-document-files-modal', {applicationId: application.id, documentId: document.id})">
                              <svg class="fill-current icon" viewBox="0 0 24 24" width="24" height="24">
                                <use xlink:href="@/assets/v2/img/sprite.svg#icon__plus"></use>
                              </svg>
                              {{ $lang.app.ADD_FILE }}
                            </button>
                          </div>
                          <div v-if="document.signDate" class="text-sm font-medium mb-4">
                            <div class="signature__panel mb1-5">
                              <div class="stack__list mb0">
                                <div class="stack__label">{{ $lang.app.SIGNED_DATE }}</div>
                                <div class="stack__value">{{ $moment(document.signDate).format('YYYY.MM.DD HH:mm') }}</div>
                              </div>
                            </div>
                            <div class="signature__panel">
                              <div class="stack__list mb0">
                                <div class="stack__label">{{ $lang.app.SIGNER }}</div>
                                <div class="stack__value">{{ document.signer }}</div>
                              </div>
                            </div>
                          </div>
                          <button v-else-if="editable" type="button"
                                  class="button button--default button--bordered button--full mb2"
                                  @click="$emitter.emit('open-sign-document-modal', {applicationId: application.id, documentId: document.id})">
                            {{ $lang.app.SIGN_DOCUMENT }}
                          </button>
                          <div v-if="editable" class="flex justify-between items-center">
                            <button  type="button" class="add__field__edit"
                                    @click="$emitter.emit('open-applicant-application-document-edit-modal', {document, documents})">
                              <svg viewBox="0 0 24 24" width="24" height="24">
                                <use xlink:href="@/assets/v2/img/sprite.svg#icon__edit"></use>
                              </svg>
                              {{ $lang.app.EDIT }}
                            </button>
                            <button type="button" @click="removeDocument(document)" :title="$lang.app.DELETE">
                              <svg viewBox="0 0 24 24" width="24" height="24">
                                <use xlink:href="@/assets/v2/img/sprite.svg#icon__remove"></use>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </template>
                    </Collapse>
                  </div>
                </div>
              </div>
              <DocumentCreateModal/>
              <DocumentEditModal/>
              <SignDocumentModal/>
              <SignAllDocumentsModal/>
              <AddDocumentFilesModal/>

              <h4 class="mt-8 add__fields__title space__between">{{ $lang.app.INITIAL_PERMIT_DOCUMENTATION }}</h4>
              <div class="add__field__list">
                <div class="row">
                  <div v-for="documentation in documentations" :key="documentation.id" class="w-full">
                    <Collapse class="add__field__item">
                      <template v-slot:title>
                        <div class="add__field__header mb-0">
                          {{ documentation.name }}
                        </div>
                      </template>
                      <template v-slot:content>
                        <div>
                          <div class="stack__list">
                            <div class="stack__label">{{ $lang.app.FILE }}</div>
                            <button type="button" class="file__item" @click="loadDocumentationFile(documentation)">
                              <svg viewBox="0 0 24 24" width="24" height="24">
                                <use xlink:href="@/assets/v2/img/sprite.svg#icon__file__pdf"></use>
                              </svg>
                              <span class="file__item__name">
                            {{ documentation.file.filename }}
                          </span>
                            </button>
                          </div>
                          <div v-if="editable" class="flex justify-end">
                            <button type="button" :title="$lang.app.DELETE"
                                    @click="removeDocumentation(documentation)">
                              <svg viewBox="0 0 24 24" width="24" height="24">
                                <use xlink:href="@/assets/v2/img/sprite.svg#icon__remove"></use>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </template>
                    </Collapse>
                  </div>
                </div>
              </div>
              <div v-if="editable" class="input__group mb0">
                <button type="button" class="button button__icon--left button--default button--bordered button__add__5__exodus__doc"
                        @click="$emitter.emit('open-applicant-application-documentation-create-modal')">
                  <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
                    <use xlink:href="@/assets/v2/img/sprite.svg#icon__plus"></use>
                  </svg>
                  <span class="button__text">{{ $lang.app.INITIAL_PERMIT_DOCUMENTATION }}</span>
                </button>
              </div>
              <DocumentationCreateModal/>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div class="application-create__controls">
      <div class="container-fluid">
        <div class="application-create__controls__wrapper">
          <button type="button" @click="$router.go(-1)" class="button button--cancel">{{ $lang.app.CANCEL }}</button>
          <router-link v-if="application.agreement" :to="{name: 'ApplicantApplicationAgreementDetail', params: {applicationId: application.id}}"
                       class="button button__icon--right">{{ $lang.app.NEXT }}
            <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
              <use xlink:href="@/assets/v2/img/sprite.svg#arrow__right"></use>
            </svg>
          </router-link>
          <router-link v-else :to="{name: 'ApplicantApplicationAgreementCreate', params: {applicationId: application.id}}"
                       class="button button__icon--right">{{ $lang.app.NEXT }}
            <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
              <use xlink:href="@/assets/v2/img/sprite.svg#arrow__right"></use>
            </svg>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import applicationApi from "../../../../api/applicant/application";
import SignDocumentModal from "../../../../components/applicant/application/modals/SignDocumentModal";
import SignAllDocumentsModal from "../../../../components/applicant/application/modals/SignAllDocumentsModal";
import AddDocumentFilesModal from "../../../../components/applicant/application/modals/AddDocumentFilesModal";
import DocumentCreateModal from "../../../../components/applicant/application/modals/DocumentCreateModal";
import DocumentationCreateModal from "../../../../components/applicant/application/modals/DocumentationCreateModal";
import DocumentEditModal from "../../../../components/applicant/application/modals/DocumentEditModal";
import fileMixin from "../../../../mixins/fileMixin";
import Collapse from "../../../../components/Collapse.vue";

export default {
  name: "ApplicantApplicationDocumentsDetail",
  props: {
    application: Object
  },
  components: {
    Collapse,
    SignDocumentModal,
    SignAllDocumentsModal,
    AddDocumentFilesModal,
    DocumentCreateModal,
    DocumentationCreateModal,
    DocumentEditModal,
  },
  mixins: [fileMixin],
  computed: {
    editable() {
      return this.application.actions.some(
          action => action.constName === 'APPLICANT_EDIT_ACTION' || action.constName === 'DEVELOPER_EDIT_ACTION'
      )
    },
    documents() {
      return this.application.documents
    },
    documentations() {
      return this.application.initialPermitDocumentations
    }
  },
  methods: {
    loadDocumentFile(document, documentFile) {
      this.$emitter.emit('open-loader-modal')
      applicationApi
          .getDocumentFile(this.application.id, document.id, documentFile.id)
          .then(({data})=> {
            const blob = this.base64toBlob(data.base64, data.mimeType);
            this.$emitter.emit('open-blob-file-modal', {
              file: data,
              blobFile: URL.createObjectURL(blob)
            })
          })
          .catch((error) => {
            this.$snotify.error(error);
          })
          .finally(() => {
            this.$emitter.emit('close-loader-modal')
          });
    },
    loadDocumentationFile(documentation) {
      this.$emitter.emit('open-loader-modal')
      applicationApi
          .getDocumentationFile(this.application.id, documentation.id)
          .then(({data})=> {
            const blob = this.base64toBlob(data.base64, data.mimeType);
            this.$emitter.emit('open-blob-file-modal', {
              file: data,
              blobFile: URL.createObjectURL(blob)
            })
          })
          .catch((error) => {
            this.$snotify.error(error);
          })
          .finally(() => {
            this.$emitter.emit('close-loader-modal')
          });
    },
    removeDocumentFile(documentFile) {
      this.$swal.fire({
        icon: 'warning',
        text: this.$lang.app.ARE_YOU_SURE_TO_DELETE,
        showDenyButton: true,
        confirmButtonColor: '#4c9ac7',
        denyButtonColor: '#6e7881',
        confirmButtonText: this.$lang.app.YES,
        denyButtonText: this.$lang.app.NO,
      }).then((result) => {
        if (result.isConfirmed) {
          applicationApi.deleteDocumentFile(this.application.id, documentFile.id)
              .then(() => {
                this.$emitter.emit('refresh-application', {reload: false})
                this.$snotify.success(this.$lang.app.SUCCESS_ACTION)
              })
              .catch(error => {
                this.$snotify.error(error)
              })
        }
      })
    },
    removeDocument(document) {
      this.$swal.fire({
        icon: 'warning',
        text: this.$lang.app.ARE_YOU_SURE_TO_DELETE,
        showDenyButton: true,
        confirmButtonColor: '#4c9ac7',
        denyButtonColor: '#6e7881',
        confirmButtonText: this.$lang.app.YES,
        denyButtonText: this.$lang.app.NO,
      }).then((result) => {
        if (result.isConfirmed) {
          applicationApi.deleteDocument(this.application.id, document.id)
              .then(() => {
                this.$emitter.emit('refresh-application', {reload: false})
                this.$snotify.success(this.$lang.app.SUCCESS_ACTION)
              })
              .catch(error => {
                this.$snotify.error(error)
              })
        }
      })
    },
    removeDocumentation(documentation) {
      this.$swal.fire({
        icon: 'warning',
        text: this.$lang.app.ARE_YOU_SURE_TO_DELETE,
        showDenyButton: true,
        confirmButtonColor: '#4c9ac7',
        denyButtonColor: '#6e7881',
        confirmButtonText: this.$lang.app.YES,
        denyButtonText: this.$lang.app.NO,
      }).then((result) => {
        if (result.isConfirmed) {
          applicationApi.deleteDocumentation(this.application.id, documentation.id)
              .then(() => {
                this.$emitter.emit('refresh-application', {reload: false})
                this.$snotify.success(this.$lang.app.SUCCESS_ACTION)
              })
              .catch(error => {
                this.$snotify.error(error)
              })
        }
      })
    }
  }
};
</script>
